import { type FirebaseApp, initializeApp } from 'firebase/app';
import AccountModule from './modules/account';
import AnalyticsModule from './modules/analytics';
import AppCheckModule from './modules/app-check';
import CashierModule from './modules/cashier';
import ConfigModule from './modules/config';
import DbModule from './modules/db';
import DirectMessageModule from './modules/direct-message';
import DomainModule from './modules/domain';
import ModActivityModule from './modules/mod-activity';
import { signUrl } from './modules/moonpay';
import NotificationModule from './modules/notification';
import PublicSettingsModule from './modules/public-settings';
import ReportModule from './modules/report';
import SearchModule from './modules/search';
import ShortUrlModule from './modules/short-url';
import StorageModule from './modules/storage';
import SuperCommentModule from './modules/super-comment';
import TokenModule from './modules/token';
import TokenPriceModule from './modules/token-price';
import VotingModule from './modules/voting';
import WalletModule from './modules/wallet';
import * as apiService from './network/api-service';

export interface ApiResponse<T> {
    status: 'ok' | 'error';
    context: string;
    message: string;
    payload: T;
}

export interface FirebaseConfig {
    apiKey: string;
    authDomain: string;
    databaseURL: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId: string;
}

export interface CreatorSDKConfig {
    rpcBaseUrl: string;
    operatorName: string;
    domainName: string;
    operatorSalt: string;
    contractName: string;
    registryContractName: string;

    cloudRunBaseUrl: string;
    functionsBaseUrl: string;
    firebaseConfig: FirebaseConfig;
    enableAnalytics: boolean;
    appCheckRecaptchaKey: string;
    enableAppCheckDebugToken: boolean;

    upvote: {
        contractName: string;
    };
}

export default class CreatorSDK {
    operatorName: string;
    domainName: string;
    operatorSalt: string;
    app: FirebaseApp;

    // Modules
    accountModule: AccountModule;
    appCheckModule: AppCheckModule;
    analyticsModule: AnalyticsModule | undefined;
    private dbModule: DbModule;
    storageModule: StorageModule;
    configModule: ConfigModule;
    votingModule: VotingModule;
    tokenModule: TokenModule;
    tokenPriceModule: TokenPriceModule;
    superCommentModule: SuperCommentModule;
    wallet: WalletModule;
    notificationModule: NotificationModule;
    directMessageModule: DirectMessageModule;
    cashierModule: CashierModule;
    searchModule: SearchModule;
    domainModule: DomainModule;
    publicSettingsModule: PublicSettingsModule;
    reportModule: ReportModule;
    modActivityModule: ModActivityModule;
    shortUrlModule: ShortUrlModule;

    constructor(config: CreatorSDKConfig) {
        const {
            appCheckRecaptchaKey,
            enableAnalytics = false,
            enableAppCheckDebugToken,
            operatorName,
            domainName,
            operatorSalt,
            contractName,
            functionsBaseUrl,
            upvote,
            firebaseConfig,
            cloudRunBaseUrl,
        } = config;
        this.domainName = domainName;
        this.operatorName = operatorName;
        this.operatorSalt = operatorSalt;
        this.app = initializeApp(firebaseConfig);

        this.appCheckModule = new AppCheckModule(this.app, appCheckRecaptchaKey, enableAppCheckDebugToken);

        apiService.init(functionsBaseUrl, cloudRunBaseUrl);

        this.dbModule = new DbModule(this.app);
        this.accountModule = new AccountModule(this.app, contractName, domainName, this.appCheckModule, this.dbModule);
        this.storageModule = new StorageModule(this.app, domainName, firebaseConfig.storageBucket, this.accountModule);
        this.configModule = new ConfigModule(this.app, domainName, this.storageModule);

        if (enableAnalytics) this.analyticsModule = new AnalyticsModule(this.app, domainName, this.appCheckModule, this.accountModule);

        this.votingModule = new VotingModule(this.app, contractName, domainName, this.appCheckModule, this.dbModule, this.accountModule);
        this.notificationModule = new NotificationModule(this.app, contractName, domainName, this.appCheckModule, this.dbModule, this.accountModule);
        this.directMessageModule = new DirectMessageModule(this.app, contractName, domainName, this.appCheckModule, this.dbModule, this.accountModule);
        this.cashierModule = new CashierModule(this.app, contractName, domainName, this.appCheckModule, this.dbModule, this.accountModule);
        this.searchModule = new SearchModule(this.app, contractName, domainName, this.appCheckModule, this.dbModule, this.accountModule);
        this.tokenModule = new TokenModule(this.app, contractName, domainName, this.appCheckModule, this.dbModule, this.storageModule, this.accountModule);
        this.tokenPriceModule = new TokenPriceModule(this.dbModule);
        this.superCommentModule = new SuperCommentModule(
            this.app,
            upvote.contractName,
            domainName,
            this.appCheckModule,
            this.dbModule,
            this.storageModule,
            this.accountModule,
            this.tokenModule
        );
        this.wallet = new WalletModule(this.app, upvote.contractName, domainName, this.appCheckModule, this.dbModule, this.storageModule, this.accountModule, this.tokenModule);
        this.domainModule = new DomainModule(this.app, contractName, domainName, this.appCheckModule, this.dbModule, this.storageModule);
        this.publicSettingsModule = new PublicSettingsModule(this.app, contractName, domainName, this.appCheckModule, this.dbModule, this.storageModule);
        this.reportModule = new ReportModule(
            this.app,
            upvote.contractName,
            domainName,
            this.appCheckModule,
            this.dbModule,
            this.storageModule,
            this.accountModule,
            this.tokenModule
        );
        this.modActivityModule = new ModActivityModule(
            this.app,
            upvote.contractName,
            domainName,
            this.appCheckModule,
            this.dbModule,
            this.storageModule,
            this.accountModule,
            this.tokenModule
        );
        this.shortUrlModule = new ShortUrlModule(
            this.app,
            upvote.contractName,
            domainName,
            this.appCheckModule,
            this.dbModule,
            this.storageModule,
            this.accountModule,
            this.tokenModule
        );
    }

    // TODO - should be part of the SDK?
    async signMoonpayUrl(url: string): Promise<string> {
        const appCheckToken = await this.appCheckModule.getAppCheckToken();
        const token = await this.accountModule.getIdToken();
        const res = await signUrl(url, appCheckToken, token);
        return res.payload.signedUrl;
    }

    getApp() {
        return this.app;
    }

    getAuth() {
        return this.accountModule.getAuth();
    }

    getOperatorName() {
        return this.operatorName;
    }

    getOperatorSalt() {
        return this.operatorSalt;
    }
}
